import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import calendarIcon from 'assets/calendar.svg';
import locationIcon from 'assets/customerManagement/address.svg';
import offerIcon from 'assets/customerManagement/offer.svg';
import editIcon from 'assets/profileHeader/edit.svg';
import arrowRight from 'assets/right-black.svg';
import appointmentIcon from 'assets/singleTicket/appointment.png';
import durationIcon from 'assets/singleTicket/appointment/duration.svg';
import emailIcon from 'assets/singleTicket/appointment/email.svg';
import frequencyIcon from 'assets/singleTicket/appointment/frequency.svg';
import phoneCallIcon from 'assets/singleTicket/appointment/phone-call.svg';
import reminderIcon from 'assets/singleTicket/appointment/reminder.svg';
import serviceIcon from 'assets/singleTicket/appointment/service.svg';
import textMessageIcon from 'assets/singleTicket/appointment/text-message.svg';
import timezoneIcon from 'assets/singleTicket/appointment/timezone.svg';
import assignees from 'assets/singleTicket/assignees.png';
import attachment from 'assets/singleTicket/attachment.png';
import customerSvg from 'assets/singleTicket/customerSvg.svg';
import dates from 'assets/singleTicket/date.png';
import nestedIndicator from 'assets/singleTicket/nested-indicator.svg';
import parentTask from 'assets/singleTicket/parent-task.png';
import project from 'assets/singleTicket/project.png';
import subproject from 'assets/singleTicket/subproject.png';
import subtaskIcon from 'assets/singleTicket/subproject.png';
import watchers from 'assets/singleTicket/watchers.png';
import fileIcon from 'assets/tickets/file.svg';
import Button from 'common-ui/button';
import { Column } from 'common-ui/dragDropUploadFile/DragDropUploadFile.styles';
import { LoadContainer, LoadingIcon } from 'common-ui/table/Table.styles';
import loadIcon from 'common-ui/table/loading.svg';
import {
  getCustomerData,
  getSingleAppointment,
} from 'features/projectManagement/ProjectManagementActions';
import {
  selectCustomerData,
  selectCustomerDataLoading,
  selectLoading,
  selectSingleAppointment,
} from 'features/projectManagement/ProjectManagementSlice';
import AvatarGroup from 'pages/components/avatarGroup';
import { baseURL, formatFileSize } from 'utils';
import { formatDateTime } from 'utils/dateUtils';

import Comment from '../comment';
import {
  AssigneeWatcherWrapper,
  AttachmentText,
  AttachmentWrapper,
  BackToListBtn,
  ButtonsWrapper,
  ColumnContainer,
  ColumnInfo,
  CommentArea,
  Container,
  Content,
  ContractItem,
  ContractsList,
  CustomerInfo,
  CustomerInfoWrapper,
  DescriptionView,
  DetailHeaderTitle,
  DetailInfo,
  DetailTitle,
  DetailsRow,
  FieldItemRow,
  HeaderContent,
  Icon,
  NavigationWrapper,
  NestedIcon,
  ParentTaskNavigationWrapper,
  RowWithSpace,
  SectionHeader,
  TabButton,
  TabContainer,
  Title,
} from './SingleAppointments.styles';
import History from './history';

const SingleAppointments = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('details');

  const appointment = useSelector(selectSingleAppointment);
  const loading = useSelector(selectLoading);
  const isLoading = loading?.singleAppointment;
  const customerData = useSelector(selectCustomerData);
  const customerLoading = useSelector(selectCustomerDataLoading);
  const { uuid } = useParams();
  const navigate = useNavigate();
  const appointmentRef = useRef(null);

  const dispatch = useDispatch();

  const handleNavigateEdit = () => {
    navigate(`/project-management/appointment/create-edit/${uuid}`);
  };

  const handleFilePreview = async (file) => {
    try {
      const fileUrl = `${baseURL.slice(0, -1)}${file.link}`;

      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleNavigateToParentChildTask = (id) => {
    navigate(`/project-management/appointment/${id}`);
  };

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/project-management/tickets');
    }
  };

  const handleNavigationToCustomerPage = (id) => {
    navigate(`/customer-relationship-management/${id}`);
  };

  useEffect(() => {
    dispatch(getSingleAppointment(uuid));
  }, [uuid]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (appointment?.customerId) {
      dispatch(getCustomerData(appointment?.customerId));
    }
  }, [dispatch, appointment?.customerId]);

  if (isLoading)
    return (
      <LoadContainer>
        <LoadingIcon src={loadIcon} alt="Loading..." />
      </LoadContainer>
    );
  return (
    <>
      <NavigationWrapper>
        <BackToListBtn onClick={handleBack}>{'< Back'}</BackToListBtn>
        {windowWidth < 1401 && (
          <TabContainer>
            <TabButton
              $isActive={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
            >
              Details
            </TabButton>
            <TabButton
              onClick={() => setActiveTab('history')}
              $isActive={activeTab === 'history'}
            >
              History
            </TabButton>
          </TabContainer>
        )}
      </NavigationWrapper>
      <Container>
        {(windowWidth > 1400 || (windowWidth < 1401 && activeTab === 'details')) && (
          <ColumnContainer>
            <Content>
              {appointment?.parentTask?.uuid && (
                <ParentTaskNavigationWrapper
                  onClick={() =>
                    handleNavigateToParentChildTask(appointment?.parentTask?.uuid)
                  }
                >
                  <DetailsRow>
                    <DetailHeaderTitle>Parent task</DetailHeaderTitle>
                    <DetailInfo>
                      ID - {appointment?.parentTask?.id} / {appointment?.parentTask?.title}
                    </DetailInfo>
                  </DetailsRow>
                  <Icon src={arrowRight} alt="right" />
                </ParentTaskNavigationWrapper>
              )}
              <HeaderContent>
                <RowWithSpace>
                  {/* <Title>{appointment?.title}</Title> */}
                  <Title>
                    {appointment?.location
                      ? appointment.location.length > 30
                        ? `${appointment.location.slice(0, 30)}...`
                        : appointment.location
                      : '-'}
                  </Title>
                  <ButtonsWrapper>
                    <Button outlined icon={editIcon} onClick={handleNavigateEdit}>
                      Edit
                    </Button>
                  </ButtonsWrapper>
                </RowWithSpace>
                <DetailsRow>
                  <DetailHeaderTitle>Appointment ID:</DetailHeaderTitle>
                  <DetailInfo>{appointment?.id}</DetailInfo>
                </DetailsRow>
                <DetailsRow>
                  <DetailHeaderTitle>Created By:</DetailHeaderTitle>
                  <DetailInfo>
                    {appointment?.createdBy?.name} {appointment?.createdBy?.surname}
                  </DetailInfo>
                </DetailsRow>
                <DetailsRow>
                  <DetailHeaderTitle>Created date:</DetailHeaderTitle>
                  <DetailInfo>{formatDateTime(appointment?.createdAt)}</DetailInfo>
                </DetailsRow>
              </HeaderContent>
              <RowWithSpace>
                <ColumnInfo>
                  <FieldItemRow>
                    <Icon src={project} alt="project" />
                    <DetailTitle>Project</DetailTitle>
                    <DetailInfo>{appointment?.project?.name || '-'}</DetailInfo>
                  </FieldItemRow>
                  <FieldItemRow>
                    <Icon src={subproject} alt="subproject" />
                    <DetailTitle>Subproject</DetailTitle>
                    <DetailInfo>{appointment?.subproject?.name || '-'}</DetailInfo>
                  </FieldItemRow>
                  {appointment?.assignees?.length > 0 ? (
                    <AssigneeWatcherWrapper>
                      <DetailsRow>
                        <Icon src={assignees} alt="assignees" />
                        <DetailTitle>Assignees</DetailTitle>
                      </DetailsRow>
                      <AvatarGroup data={appointment?.assignees} />
                      {console.log(appointment)}
                    </AssigneeWatcherWrapper>
                  ) : (
                    <FieldItemRow>
                      <Icon src={assignees} alt="assignees" />
                      <DetailTitle>Assignees</DetailTitle>
                      <DetailInfo>-</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.customerId != null && appointment?.customerId !== '' && (
                    <CustomerInfoWrapper>
                      <FieldItemRow>
                        <NestedIcon src={customerSvg} alt="customer" />
                        <CustomerInfo
                          $disabled={
                            customerData?.contracts && customerData.contracts.length > 1
                          }
                          onClick={() => {
                            if (
                              customerData?.contracts &&
                              customerData.contracts.length === 1
                            ) {
                              handleNavigationToCustomerPage(customerData.contracts[0]);
                            } else if (
                              !customerData?.contracts ||
                              customerData.contracts.length === 0
                            ) {
                              handleNavigationToCustomerPage(
                                customerData?.customerId || '7034234'
                              );
                            }
                          }}
                        >
                          {customerLoading
                            ? 'Loading customer data...'
                            : customerData
                              ? `Customer ERP ID: ${customerData.customerId}, ${customerData.fullName}: +${customerData.phoneNumbers?.[0] || 'N/A'}`
                              : `Customer ERP ID: ${appointment?.customerId}`}
                        </CustomerInfo>
                      </FieldItemRow>
                      {customerData?.contracts && customerData.contracts.length > 1 && (
                        <ContractsList>
                          {customerData.contracts.map((contractId, index) => (
                            <ContractItem
                              key={contractId}
                              onClick={() => handleNavigationToCustomerPage(contractId)}
                            >
                              <NestedIcon style={{ marginTop: -12 }} src={nestedIndicator} />
                              Contract {index + 1}: {contractId}
                            </ContractItem>
                          ))}
                        </ContractsList>
                      )}
                    </CustomerInfoWrapper>
                  )}
                </ColumnInfo>
                <ColumnInfo>
                  {appointment?.watchers?.length > 0 && (
                    <AssigneeWatcherWrapper>
                      <DetailsRow>
                        <Icon src={watchers} alt="watchers" />
                        <DetailTitle>Watchers</DetailTitle>
                      </DetailsRow>
                      <AvatarGroup data={appointment?.watchers} />
                    </AssigneeWatcherWrapper>
                  )}
                  {appointment?.selectedOffer && (
                    <FieldItemRow>
                      <Icon src={offerIcon} alt="current offer" />
                      <DetailTitle>Current Offer</DetailTitle>
                      <DetailInfo>{appointment.selectedOffer}</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.newOffers && (
                    <FieldItemRow>
                      <Icon src={offerIcon} alt="new offers" />
                      <DetailTitle>New Offers</DetailTitle>
                      <DetailInfo>{appointment.newOffers}</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.parentTask?.title && (
                    <FieldItemRow>
                      <Icon src={parentTask} alt="parent task" />
                      <DetailTitle>Parent task</DetailTitle>
                      <DetailInfo>
                        ID - {appointment?.parentTask?.id} / {appointment?.parentTask?.title}
                      </DetailInfo>
                    </FieldItemRow>
                  )}
                </ColumnInfo>
              </RowWithSpace>
              <SectionHeader>
                <Icon src={calendarIcon} alt="calendar" />
                Appointments
              </SectionHeader>
              <RowWithSpace>
                <ColumnInfo>
                  <FieldItemRow>
                    <Icon src={locationIcon} alt="location" />
                    <DetailTitle>Location</DetailTitle>
                    <DetailInfo>
                      {appointment?.location
                        ? appointment.location.length > 30
                          ? `${appointment.location.slice(0, 30)}...`
                          : appointment.location
                        : '-'}
                    </DetailInfo>
                  </FieldItemRow>
                  <FieldItemRow>
                    <Icon src={serviceIcon} alt="service" />
                    <DetailTitle>Service</DetailTitle>
                    <DetailInfo>{appointment?.service || '-'}</DetailInfo>
                  </FieldItemRow>
                  <FieldItemRow>
                    <Icon src={timezoneIcon} alt="timezone" />
                    <DetailTitle>Timezone</DetailTitle>
                    <DetailInfo>{appointment?.timezone?.name || '-'}</DetailInfo>
                  </FieldItemRow>
                  <FieldItemRow>
                    <Icon src={frequencyIcon} alt="frequency" />
                    <DetailTitle>Frequency</DetailTitle>
                    <DetailInfo>{appointment?.frequency || '-'}</DetailInfo>
                  </FieldItemRow>
                  {appointment?.date && (
                    <FieldItemRow>
                      <Icon src={dates} alt="date" />
                      <DetailTitle>Date</DetailTitle>
                      <DetailInfo>{formatDateTime(appointment.date)}</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.time && (
                    <FieldItemRow>
                      <Icon src={dates} alt="time" />
                      <DetailTitle>Time</DetailTitle>
                      <DetailInfo>{appointment.time}</DetailInfo>
                    </FieldItemRow>
                  )}
                </ColumnInfo>
                <ColumnInfo>
                  {appointment?.duration && (
                    <FieldItemRow>
                      <Icon src={durationIcon} alt="duration" />
                      <DetailTitle>Duration</DetailTitle>
                      <DetailInfo>{appointment.duration} min</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.reminder && (
                    <FieldItemRow>
                      <Icon src={reminderIcon} alt="reminder" />
                      <DetailTitle>Reminder</DetailTitle>
                      <DetailInfo>{appointment.reminder} min</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.communicationMethod?.phoneCall && (
                    <FieldItemRow>
                      <Icon src={phoneCallIcon} alt="phone call" />
                      <DetailTitle>Phone Call</DetailTitle>
                      <DetailInfo>{appointment.communicationMethod.phoneCall}</DetailInfo>
                    </FieldItemRow>
                  )}
                  {appointment?.communicationMethod?.textMessage &&
                    appointment.communicationMethod.textMessage !== '+374' &&
                    appointment.communicationMethod.textMessage !== '374' && (
                      <FieldItemRow>
                        <Icon src={textMessageIcon} alt="text message" />
                        <DetailTitle>Text Message</DetailTitle>
                        <DetailInfo>{appointment.communicationMethod.textMessage}</DetailInfo>
                      </FieldItemRow>
                    )}
                  {appointment?.communicationMethod?.email && (
                    <FieldItemRow>
                      <Icon src={emailIcon} alt="email" />
                      <DetailTitle>Email</DetailTitle>
                      <DetailInfo>{appointment.communicationMethod.email}</DetailInfo>
                    </FieldItemRow>
                  )}
                </ColumnInfo>
              </RowWithSpace>
              {appointment?.description && (
                <CommentArea>
                  <DescriptionView
                    dangerouslySetInnerHTML={{ __html: appointment?.description }}
                  ></DescriptionView>
                </CommentArea>
              )}

              {appointment?.files?.length > 0 && (
                <AttachmentText>
                  <Icon src={attachment} alt="attachment" />
                  Attachments {`(${appointment?.files?.length})`}
                </AttachmentText>
              )}
              <FieldItemRow className="uploaded-files-wrapper">
                {appointment?.files?.map((item, index) => (
                  <AttachmentWrapper key={index} onClick={() => handleFilePreview(item)}>
                    <Icon
                      src={fileIcon}
                      alt="file"
                      style={{ width: '18px', height: '22px' }}
                    />
                    <Column>
                      <p>{item.name}</p>
                      <p>{formatFileSize(item.size)}</p>
                    </Column>
                  </AttachmentWrapper>
                ))}
              </FieldItemRow>
              {appointment?.childTickets?.length > 0 && (
                <>
                  <AttachmentText>
                    <Icon src={subtaskIcon} />
                    Subtasks
                  </AttachmentText>
                  {appointment?.childTickets?.map((childTicket) => (
                    <ParentTaskNavigationWrapper
                      key={childTicket?.uuid}
                      onClick={() => handleNavigateToParentChildTask(childTicket?.uuid)}
                    >
                      <DetailsRow>
                        <DetailInfo>
                          ID - {childTicket?.id} / {childTicket?.title}
                        </DetailInfo>
                      </DetailsRow>
                      <Icon src={arrowRight} alt="right" />
                    </ParentTaskNavigationWrapper>
                  ))}
                </>
              )}
              {appointment?.appointment && (
                <div ref={appointmentRef}>
                  <AttachmentText>
                    <Icon src={appointmentIcon} alt="appointment" />
                    Appointment
                  </AttachmentText>
                </div>
              )}
            </Content>
            <Comment type="appointment" />
          </ColumnContainer>
        )}

        {(windowWidth > 1400 || (windowWidth < 1401 && activeTab === 'history')) && (
          <History />
        )}
      </Container>
    </>
  );
};

export default SingleAppointments;
